import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Drawer, Box, Typography, IconButton, CircularProgress, Alert, InputBase, TextField, Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import SearchIcon from '@mui/icons-material/Search';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import BlockIcon from '@mui/icons-material/Block';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import ReactMarkdown from 'react-markdown';

import { colors, typography, shadows, gradient } from '../theme/designTheme';
import { useDraftReport, usePatchReportSection, useFinalizeReport } from '../hooks/useDraftReport';

const STATUSES = [
  { key: 'drafted',         label: 'Drafted',         pip: colors.warn,            bg: colors.warnBg,           fg: colors.warn,             desc: 'Written by agent, not yet reviewed' },
  { key: 'to-review',       label: 'To Review',       pip: colors.primary,         bg: colors.primaryContainer, fg: colors.onPrimaryContainer, desc: 'Flagged for your attention' },
  { key: 'reviewed',        label: 'Reviewed',        pip: colors.success,         bg: colors.successContainer, fg: colors.success,          desc: 'You’ve checked and approved it' },
  { key: 'needs-revision',  label: 'Needs Revision',  pip: colors.critical,        bg: colors.errorContainer,   fg: colors.onErrorContainer, desc: 'Regenerate or edit before signing off' },
  { key: 'pending',         label: 'Pending',         pip: colors.outlineVariant,  bg: colors.canvas,           fg: colors.textSecondary,    desc: 'Not yet drafted' },
];

const EVIDENCE_TAG_STYLE = {
  asic:         { bg: colors.primaryContainer, fg: colors.onPrimaryContainer, label: 'ASIC' },
  credit:       { bg: colors.primaryContainer, fg: colors.onPrimaryContainer, label: 'Credit' },
  asic_credit:  { bg: colors.primaryContainer, fg: colors.onPrimaryContainer, label: 'ASIC · Credit' },
  corelogic:    { bg: colors.purpleBg,         fg: colors.purple,              label: 'CoreLogic' },
  transactions: { bg: colors.errorContainer,   fg: colors.onErrorContainer,    label: 'Transactions' },
  abr:          { bg: colors.warnBg,           fg: colors.warn,                label: 'ABR / GST' },
  abr_gst:      { bg: colors.warnBg,           fg: colors.warn,                label: 'ABR / GST' },
  network:      { bg: colors.successContainer, fg: colors.success,             label: 'Network' },
  default:      { bg: colors.canvas,           fg: colors.textSecondary,       label: 'Source' },
};

const storageKey = (caseNumber) => `lisa:report-status:${caseNumber}`;

const formatRelativeTime = (iso) => {
  if (!iso) return null;
  const seconds = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
};

// ── Status pip (in menu + in chip) ────────────────────────────────────

const StatusChip = ({ statusKey, onChange, openMenu, onToggle }) => {
  const st = STATUSES.find((s) => s.key === statusKey) || STATUSES[0];
  const chipRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null);

  // Position the portal menu below+right-aligned to the chip, using viewport coords.
  useLayoutEffect(() => {
    if (!openMenu || !chipRef.current) { setMenuPos(null); return; }
    const place = () => {
      const r = chipRef.current.getBoundingClientRect();
      setMenuPos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [openMenu]);

  const menu = openMenu && menuPos && createPortal(
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{
        position: 'fixed', top: menuPos.top, right: menuPos.right, zIndex: 2000,
        bgcolor: colors.surface, borderRadius: '10px',
        p: 0.75, minWidth: 220, boxShadow: shadows.elevated,
        border: `1px solid ${colors.canvas}`,
      }}
    >
      <Typography sx={{
        fontFamily: typography.fontFamilyDisplay, fontSize: '9.5px', fontWeight: 700,
        letterSpacing: '0.12em', textTransform: 'uppercase',
        color: colors.textSecondary, px: 1, pt: 0.5, pb: 0.5,
      }}>
        Set status
      </Typography>
      {STATUSES.map((opt, idx) => (
        <Box key={opt.key}>
          {idx === 3 && <Box sx={{ height: '1px', bgcolor: colors.canvas, my: 0.5, mx: 0.25 }} />}
          <Box
            onClick={() => onChange(opt.key)}
            sx={{
              display: 'grid', gridTemplateColumns: '14px 1fr 14px', gap: 1, alignItems: 'center',
              p: '8px 8px', borderRadius: '7px',
              fontFamily: typography.fontFamily, fontSize: '12.5px', fontWeight: 500, letterSpacing: '-0.002em',
              color: colors.textPrimary, cursor: 'pointer',
              '&:hover': { bgcolor: colors.canvas },
            }}
          >
            <Box component="span" sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: opt.pip, display: 'inline-block' }} />
            <Box>
              {opt.label}
              <Typography component="span" sx={{ display: 'block', fontSize: '10.5px', color: colors.textSecondary, fontWeight: 400, mt: 0.25, letterSpacing: 0 }}>
                {opt.desc}
              </Typography>
            </Box>
            {opt.key === statusKey ? <CheckIcon sx={{ fontSize: 12, color: colors.primary }} /> : <Box />}
          </Box>
        </Box>
      ))}
    </Box>,
    document.body,
  );

  return (
    <>
      <Box
        ref={chipRef}
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.625,
          fontFamily: typography.fontFamilyDisplay,
          fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
          px: 0.625, py: '3px', borderRadius: '4px',
          color: colors.textSecondary,
          cursor: 'pointer', userSelect: 'none',
          transition: 'background-color 0.12s, color 0.12s',
          '&:hover': { bgcolor: colors.canvas, color: colors.textPrimary },
        }}
      >
        <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: st.pip }} />
        {st.label}
        <Box component="svg" sx={{ width: 7, height: 7, opacity: 0.5, ml: 0.125 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4}>
          <polyline points="6 9 12 15 18 9" />
        </Box>
      </Box>
      {menu}
    </>
  );
};

// ── Collapsible section ───────────────────────────────────────────────

const Section = ({ section, numberLabel, statusKey, setStatus, openMenuKey, setOpenMenuKey, reportId, onSave, busy }) => {
  const [open, setOpen] = useState(section.order <= 2);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(section.content || '');

  useEffect(() => setDraft(section.content || ''), [section.content]);

  const menuOpen = openMenuKey === section.key;

  const save = () => {
    onSave({ reportId, sectionKey: section.key, content: draft });
    setEditing(false);
  };

  const prose = section.content;

  return (
    <Box sx={{
      borderBottom: `1px solid ${colors.canvas}`,
      p: '16px 2px',
      '&:last-child': { borderBottom: 0 },
      '&:hover .section-footer, &:focus-within .section-footer': { opacity: 1 },
    }}>
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: 'grid', gridTemplateColumns: '24px 1fr auto auto',
          gap: 1.5, alignItems: 'center', cursor: 'pointer', userSelect: 'none',
          p: '4px 0',
        }}
      >
        <Box sx={{
          fontFamily: typography.fontFamilyMono, fontSize: '10.5px', fontWeight: 700,
          letterSpacing: '0.04em',
          width: 24, height: 24, borderRadius: '50%',
          bgcolor: open ? colors.primaryContainer : colors.canvas,
          color: open ? colors.onPrimaryContainer : colors.textSecondary,
          display: 'grid', placeItems: 'center',
        }}>
          {numberLabel}
        </Box>
        <Typography sx={{
          fontFamily: typography.fontFamilyDisplay, fontWeight: 700, fontSize: '14.5px',
          letterSpacing: '-0.005em', color: colors.textPrimary,
        }}>
          {section.title}
        </Typography>
        <StatusChip
          statusKey={statusKey}
          openMenu={menuOpen}
          onToggle={() => setOpenMenuKey(menuOpen ? null : section.key)}
          onChange={(nk) => { setStatus(section.key, nk); setOpenMenuKey(null); }}
        />
        <Box sx={{
          color: open ? colors.primary : colors.outlineVariant,
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s',
          display: 'grid', placeItems: 'center',
        }}>
          <ExpandMoreIcon sx={{ fontSize: 18 }} />
        </Box>
      </Box>

      {open && (
        <Box sx={{ mt: 1.25, pl: '36px' }}>
          {editing ? (
            <Box>
              <TextField
                value={draft} onChange={(e) => setDraft(e.target.value)} multiline minRows={5} fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: colors.canvas, borderRadius: '8px',
                    fontFamily: typography.fontFamilySerif, fontSize: '14px', lineHeight: 1.65,
                    color: colors.textPrimary,
                    '& fieldset': { borderColor: colors.border },
                    '&.Mui-focused fieldset': { borderColor: colors.primary },
                  },
                }}
              />
              <Box sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'flex-end' }}>
                <Button size="small" onClick={() => { setDraft(section.content || ''); setEditing(false); }} sx={{ color: colors.textSecondary, fontSize: '11px' }}>
                  Cancel
                </Button>
                <Button size="small" onClick={save} disabled={busy} sx={{ color: colors.primary, fontSize: '11px', fontWeight: 700 }}>
                  Save
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{
              fontFamily: typography.fontFamilySerif, fontSize: '14.5px', lineHeight: 1.65,
              color: colors.textPrimary,
              '& p': { m: 0, mb: 1.25 }, '& p:last-child': { mb: 0 },
              '& mark': { bgcolor: 'rgba(0,90,196,0.1)', color: colors.textPrimary, px: 0.4, borderRadius: '3px', fontWeight: 500 },
              '& strong': { fontWeight: 600 },
            }}>
              {prose
                ? <ReactMarkdown>{prose}</ReactMarkdown>
                : <Typography sx={{ color: colors.textSecondary, fontStyle: 'italic' }}>No content yet.</Typography>
              }
            </Box>
          )}

          <Box
            className="section-footer"
            sx={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              mt: 1.5, pt: 1.25, borderTop: `1px dashed ${colors.surfaceLow}`,
              fontSize: '11.5px', color: colors.textSecondary,
              opacity: editing ? 1 : 0,
              transition: 'opacity 0.15s ease-out',
            }}
          >
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
              <AutoAwesomeIcon sx={{
                color: colors.primary, fontSize: 11,
                animation: 'sparkle-pulse 2s ease-in-out infinite',
                '@keyframes sparkle-pulse': { '0%,100%': { opacity: 0.7, transform: 'scale(1)' }, '50%': { opacity: 1, transform: 'scale(1.15)' } },
              }} />
              Agent-drafted
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button
                size="small"
                onClick={(e) => { e.stopPropagation(); setEditing((v) => !v); }}
                startIcon={<EditOutlinedIcon sx={{ fontSize: 11 }} />}
                sx={{
                  fontFamily: typography.fontFamily, fontSize: '11px', fontWeight: 600,
                  color: colors.textSecondary, textTransform: 'none', px: 1, py: 0.5, minWidth: 0,
                  '&:hover': { bgcolor: colors.canvas, color: colors.textPrimary },
                }}
              >
                Edit
              </Button>
              <Button
                size="small"
                startIcon={<ReplayIcon sx={{ fontSize: 11 }} />}
                sx={{
                  fontFamily: typography.fontFamily, fontSize: '11px', fontWeight: 600,
                  color: colors.primary, textTransform: 'none', px: 1, py: 0.5, minWidth: 0,
                  '&:hover': { bgcolor: colors.primaryContainer },
                }}
              >
                Regenerate
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// ── Verdict hero card (section 02: Recommendation) ───────────────────

const confidenceLabel = (c) => {
  const n = typeof c === 'number' ? c : 0;
  if (n >= 0.75) return 'HIGH';
  if (n >= 0.5)  return 'MEDIUM';
  return 'LOW';
};

const VerdictHero = ({ headline, summary, confidence = 0.94, actionPills = [], onAccept, onEdit, onShare }) => {
  const R = 25;
  const C = 2 * Math.PI * R; // 157.08
  const dashOffset = C * (1 - Math.max(0, Math.min(1, confidence)));

  return (
    <Box sx={{
      position: 'relative', background: gradient.verdict, color: '#fff',
      borderRadius: '14px', p: '20px 22px', mb: 1.75, overflow: 'hidden',
      boxShadow: '0 2px 6px rgba(61,14,17,0.3), 0 18px 36px rgba(61,14,17,0.18)',
    }}>
      <Box sx={{
        position: 'absolute', top: '-30%', right: '-15%',
        width: 320, height: 320, pointerEvents: 'none',
        background: 'radial-gradient(circle, rgba(255,140,140,0.22), transparent 65%)',
      }} />
      <Box sx={{
        position: 'absolute', bottom: '-40%', left: '-10%',
        width: 260, height: 260, pointerEvents: 'none',
        background: 'radial-gradient(circle, rgba(200,80,80,0.15), transparent 70%)',
      }} />

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.75 }}>
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.75,
            fontFamily: typography.fontFamilyDisplay, fontSize: '10.5px',
            letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 800,
            bgcolor: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.15)',
            p: '5px 10px', borderRadius: '6px',
          }}>
            <Box component="span" sx={{
              width: 6, height: 6, borderRadius: '50%',
              bgcolor: '#ff6b6b', boxShadow: '0 0 8px rgba(255,107,107,0.8)',
              animation: 'pulse-dot 1.6s ease-in-out infinite',
              '@keyframes pulse-dot': {
                '0%,100%': { opacity: 1, transform: 'scale(1)' },
                '50%':     { opacity: 0.6, transform: 'scale(0.85)' },
              },
            }} />
            Recommendation · Action Required
          </Box>
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 1,
            fontSize: '10.5px', color: 'rgba(255,255,255,0.6)',
            fontFamily: typography.fontFamilyMono, letterSpacing: '0.04em',
          }}>
            <Box component="span">AGGREGATED FROM 3 AGENTS</Box>
            <Box component="span" sx={{ opacity: 0.5 }}>·</Box>
            <Box component="span">2m AGO</Box>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: 2.5, alignItems: 'center' }}>
          <Box>
            <Typography sx={{
              fontFamily: typography.fontFamilyDisplay, fontWeight: 800, fontSize: '18px',
              letterSpacing: '-0.015em', lineHeight: 1.25, mb: 0.75,
            }}>
              {headline || 'Decline application and escalate applicant and linked entities for review'}
            </Typography>
            <Box sx={{
              fontFamily: typography.fontFamilySerif, fontSize: '13.5px', lineHeight: 1.55,
              color: 'rgba(255,255,255,0.82)', fontWeight: 400,
              '& strong': { color: '#fff', fontWeight: 600 },
            }}>
              {summary ? <ReactMarkdown components={{ p: ({ children }) => <>{children}</> }}>{summary}</ReactMarkdown>
                       : 'Escalate to Financial Crime for SMR consideration under the AML/CTF Act.'}
            </Box>
            {actionPills.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
                {actionPills.map((p, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 0.625,
                      fontFamily: typography.fontFamilyDisplay, fontSize: '10.5px', fontWeight: 600,
                      color: p.variant === 'red' ? '#ffd9d9' : 'rgba(255,255,255,0.85)',
                      bgcolor: p.variant === 'red' ? 'rgba(220,80,80,0.2)' : 'rgba(255,255,255,0.08)',
                      border: `1px solid ${p.variant === 'red' ? 'rgba(255,140,140,0.3)' : 'rgba(255,255,255,0.14)'}`,
                      p: '4px 9px', borderRadius: '5px', letterSpacing: '0.02em',
                    }}
                  >
                    {p.variant === 'red' && <BlockIcon sx={{ fontSize: 10, opacity: 0.8 }} />}
                    {p.label}
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Box sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75,
            pl: 2.25, borderLeft: '1px solid rgba(255,255,255,0.15)',
          }}>
            <Box sx={{ position: 'relative', width: 60, height: 60 }}>
              <Box component="svg" viewBox="0 0 60 60" sx={{ width: 60, height: 60, transform: 'rotate(-90deg)' }}>
                <circle cx="30" cy="30" r={R} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="5" />
                <circle
                  cx="30" cy="30" r={R} fill="none" stroke="#ff8a8a" strokeWidth="5"
                  strokeLinecap="round" strokeDasharray={C} strokeDashoffset={dashOffset}
                />
              </Box>
              <Box sx={{
                position: 'absolute', inset: 0, display: 'grid', placeItems: 'center',
                fontFamily: typography.fontFamilyDisplay, fontWeight: 800, fontSize: '12px', letterSpacing: '0.02em',
              }}>
                {confidenceLabel(confidence)}
              </Box>
            </Box>
            <Box sx={{
              fontFamily: typography.fontFamilyDisplay, fontSize: '9px',
              letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700,
              color: 'rgba(255,255,255,0.6)',
            }}>
              Confidence
            </Box>
          </Box>
        </Box>

        <Box sx={{
          display: 'flex', gap: 1, mt: 2, pt: 1.75,
          borderTop: '1px solid rgba(255,255,255,0.12)',
        }}>
          <Box
            component="button"
            onClick={onAccept}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.75,
              p: '8px 14px', border: 0, borderRadius: '7px',
              fontFamily: typography.fontFamily, fontWeight: 600, fontSize: '12px',
              bgcolor: '#fff', color: '#3d0e11', cursor: 'pointer',
              '&:hover': { bgcolor: '#ffe9e9' },
            }}
          >
            <CheckIcon sx={{ fontSize: 12 }} />
            Accept & finalize
          </Box>
          <Box
            component="button"
            onClick={onEdit}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.75,
              p: '8px 14px', borderRadius: '7px',
              bgcolor: 'rgba(255,255,255,0.1)', color: '#fff',
              border: '1px solid rgba(255,255,255,0.18)', cursor: 'pointer',
              fontFamily: typography.fontFamily, fontWeight: 600, fontSize: '12px',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' },
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: 12 }} />
            Edit
          </Box>
          <Box
            component="button"
            onClick={onShare}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.75,
              p: '8px 14px', borderRadius: '7px',
              bgcolor: 'rgba(255,255,255,0.1)', color: '#fff',
              border: '1px solid rgba(255,255,255,0.18)', cursor: 'pointer',
              fontFamily: typography.fontFamily, fontWeight: 600, fontSize: '12px',
              ml: 'auto',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' },
            }}
          >
            <ShareOutlinedIcon sx={{ fontSize: 12 }} />
            Share with Financial Crime
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ── Evidence rail ─────────────────────────────────────────────────────

const EvidenceCard = ({ item, cite, usedIn }) => {
  const ts = EVIDENCE_TAG_STYLE[(item.source || '').toLowerCase()] || EVIDENCE_TAG_STYLE.default;
  return (
    <Box
      draggable
      sx={{
        position: 'relative', bgcolor: colors.surface, borderRadius: '12px',
        p: '12px 14px', boxShadow: shadows.ambient, cursor: 'grab',
        transition: 'transform 0.12s, box-shadow 0.12s',
        '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 2px 4px rgba(43,52,56,0.06), 0 10px 28px rgba(43,52,56,0.06)' },
        '&:active': { cursor: 'grabbing' },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.5,
          fontFamily: typography.fontFamilyMono, fontSize: '9.5px', letterSpacing: '0.08em',
          textTransform: 'uppercase', fontWeight: 700,
          p: '3px 7px', borderRadius: '4px',
          bgcolor: ts.bg, color: ts.fg,
        }}>
          {ts.label}
        </Box>
        <Box sx={{ fontFamily: typography.fontFamilyMono, fontSize: '9.5px', color: colors.textSecondary, fontWeight: 600, letterSpacing: '0.04em' }}>
          {cite}
        </Box>
      </Box>
      <Typography sx={{ fontFamily: typography.fontFamilyDisplay, fontWeight: 700, fontSize: '12.5px', letterSpacing: '-0.005em', mb: 0.25 }}>
        {item.label || 'Evidence'}
      </Typography>
      <Typography sx={{ fontSize: '11px', color: colors.textSecondary, lineHeight: 1.4 }}>
        {item.ref || item.subtitle || '—'}
      </Typography>
      {usedIn && (
        <Box sx={{
          position: 'absolute', top: 10, right: 10,
          width: 14, height: 14, borderRadius: '50%', bgcolor: colors.success, color: '#fff',
          display: 'grid', placeItems: 'center',
        }}>
          <CheckIcon sx={{ fontSize: 8, strokeWidth: 3 }} />
        </Box>
      )}
    </Box>
  );
};

// ── Main ──────────────────────────────────────────────────────────────

const ReportEditor = ({ open, onClose, caseNumber }) => {
  const { data: report, isLoading } = useDraftReport(caseNumber, open);
  const patch = usePatchReportSection(caseNumber);
  const finalize = useFinalizeReport(caseNumber);

  const sections = useMemo(() => report?.sections ?? [], [report]);
  const attachments = useMemo(() => report?.evidence_attachments ?? [], [report]);

  const [statusMap, setStatusMap] = useState({});
  const [openMenuKey, setOpenMenuKey] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showEvidence, setShowEvidence] = useState(false);

  // Load persisted statuses (or seed from section order)
  useEffect(() => {
    if (!caseNumber) return;
    const saved = (() => {
      try { return JSON.parse(localStorage.getItem(storageKey(caseNumber)) || '{}'); }
      catch { return {}; }
    })();
    const map = {};
    sections.forEach((s, i) => {
      if (saved[s.key]) { map[s.key] = saved[s.key]; }
      else {
        // reasonable defaults: recommendation → to-review, first few → drafted, last → pending
        if (s.key === 'recommendation' || i === 1) map[s.key] = 'to-review';
        else if (i >= sections.length - 2) map[s.key] = 'pending';
        else map[s.key] = 'drafted';
      }
    });
    setStatusMap(map);
  }, [caseNumber, sections]);

  const setStatus = (key, next) => {
    setStatusMap((prev) => {
      const nmap = { ...prev, [key]: next };
      try { localStorage.setItem(storageKey(caseNumber), JSON.stringify(nmap)); } catch {}
      return nmap;
    });
  };

  // Close status menu when clicking outside panel
  useEffect(() => {
    if (!openMenuKey) return;
    const onDoc = () => setOpenMenuKey(null);
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [openMenuKey]);

  const reviewedCount = Object.values(statusMap).filter((v) => v === 'reviewed').length;
  const total = sections.length || 0;
  const pct = total ? Math.round((reviewedCount / total) * 100) : 0;

  const citedSectionKeys = useMemo(() => {
    // Map each evidence ref to whether it's cited anywhere
    const out = new Set();
    sections.forEach((s) => {
      const m = (s.content || '').match(/E\d+/g);
      if (m) m.forEach((id) => out.add(id));
    });
    return out;
  }, [sections]);

  const filteredAttachments = useMemo(() => {
    if (!searchTerm) return attachments;
    const q = searchTerm.toLowerCase();
    return attachments.filter((a) =>
      (a.label || '').toLowerCase().includes(q) ||
      (a.source || '').toLowerCase().includes(q) ||
      (a.ref || '').toLowerCase().includes(q));
  }, [attachments, searchTerm]);

  const savedLabel = report?.updated_at
    ? `Saved ${formatRelativeTime(report.updated_at)}`
    : 'Autosaved';

  return (
    <Drawer
      anchor="right" open={open} onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: { xs: '100%', md: showEvidence ? 1060 : 760 },
            bgcolor: colors.surface,
            boxShadow: shadows.panel, borderLeft: 'none',
            display: 'grid',
            gridTemplateColumns: showEvidence ? '1fr 300px' : '1fr',
            overflow: 'hidden',
            transition: 'width 0.24s ease-out',
          },
        },
      }}
    >
      {/* ── Report column ── */}
      <Box sx={{
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        borderRight: showEvidence ? `1px solid ${colors.canvas}` : 'none',
      }}>

        {/* Header */}
        <Box sx={{
          p: '18px 28px 16px', borderBottom: `1px solid ${colors.canvas}`,
          display: 'flex', flexDirection: 'column', gap: 1.75, flexShrink: 0,
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 34, height: 34, borderRadius: '10px',
                background: gradient.primary, color: '#fff',
                display: 'grid', placeItems: 'center',
                boxShadow: '0 2px 10px rgba(0,90,196,0.25)',
              }}>
                <DescriptionOutlinedIcon sx={{ fontSize: 16 }} />
              </Box>
              <Box>
                <Typography sx={{
                  fontFamily: typography.fontFamilyDisplay, fontWeight: 700, fontSize: '15px',
                  letterSpacing: '-0.005em', lineHeight: 1.2,
                }}>
                  Draft Investigation Report
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.875, alignItems: 'center', mt: 0.4, fontSize: '11px', color: colors.textSecondary, flexWrap: 'wrap' }}>
                  <Box component="span" sx={{ fontFamily: typography.fontFamilyMono, color: colors.primary, fontWeight: 600 }}>
                    REF-{caseNumber}
                  </Box>
                  <Box component="span" sx={{ color: colors.outlineVariant }}>·</Box>
                  <Box component="span" sx={{ fontFamily: typography.fontFamilyMono }}>v{report?.version || 1}</Box>
                  {report && (
                    <>
                      <Box component="span" sx={{ color: colors.outlineVariant }}>·</Box>
                      <Box component="span">
                        {(!report.created_by || report.created_by === 'AGENT')
                          ? 'Auto-generated'
                          : `by ${report.created_by}`}
                      </Box>
                    </>
                  )}
                  <Box component="span" sx={{ color: colors.outlineVariant }}>·</Box>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: colors.success, fontWeight: 500 }}>
                    <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: colors.success }} />
                    {savedLabel}
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
              <Button
                size="small"
                onClick={() => setShowEvidence((v) => !v)}
                startIcon={<InventoryOutlinedIcon sx={{ fontSize: 14 }} />}
                sx={{
                  fontFamily: typography.fontFamily, fontSize: '12px', fontWeight: 600,
                  color: showEvidence ? colors.primary : colors.textSecondary,
                  bgcolor: showEvidence ? colors.primaryContainer : 'transparent',
                  textTransform: 'none', borderRadius: '8px',
                  px: 1.25, py: 0.75, minWidth: 0,
                  '&:hover': { bgcolor: showEvidence ? colors.primaryContainer : colors.canvas, color: colors.textPrimary, filter: showEvidence ? 'brightness(0.97)' : undefined },
                }}
              >
                Evidence
                <Box component="span" sx={{
                  ml: 0.625,
                  fontFamily: typography.fontFamilyMono, fontSize: '10.5px', fontWeight: 700,
                  color: showEvidence ? colors.primary : colors.textSecondary,
                  opacity: 0.8,
                }}>
                  {attachments.length}
                </Box>
              </Button>
              <IconButton size="small" sx={{ color: colors.textSecondary, width: 32, height: 32, borderRadius: '8px', '&:hover': { bgcolor: colors.canvas, color: colors.textPrimary } }}>
                <FileDownloadOutlinedIcon sx={{ fontSize: 15 }} />
              </IconButton>
              <IconButton size="small" sx={{ color: colors.textSecondary, width: 32, height: 32, borderRadius: '8px', '&:hover': { bgcolor: colors.canvas, color: colors.textPrimary } }}>
                <HistoryOutlinedIcon sx={{ fontSize: 15 }} />
              </IconButton>
              {report && !report.is_finalized && (
                <Button
                  onClick={() => finalize.mutate({ reportId: report.id, decision: 'concur' })}
                  disabled={finalize.isPending}
                  startIcon={<CheckIcon sx={{ fontSize: 13 }} />}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.75,
                    p: '8px 14px', background: gradient.primary, color: '#fff',
                    border: 0, borderRadius: '8px',
                    fontFamily: typography.fontFamily, fontWeight: 600, fontSize: '12.5px',
                    textTransform: 'none',
                    boxShadow: '0 2px 10px rgba(0,90,196,0.25), inset 0 1px 0 rgba(255,255,255,0.15)',
                    '&:hover': { background: gradient.primary, filter: 'brightness(1.05)' },
                  }}
                >
                  Finalize
                </Button>
              )}
              <IconButton size="small" onClick={onClose} sx={{ color: colors.textSecondary, width: 32, height: 32, borderRadius: '8px', '&:hover': { bgcolor: colors.canvas, color: colors.textPrimary } }}>
                <CloseIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Box>
          </Box>

          {/* Review progress */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            p: '8px 12px', bgcolor: colors.canvas, borderRadius: '10px',
            fontSize: '11.5px', color: colors.textSecondary,
          }}>
            <Typography sx={{
              fontFamily: typography.fontFamilyDisplay, fontWeight: 700,
              fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.textPrimary,
            }}>
              Review Progress
            </Typography>
            <Box sx={{ flex: 1, height: 4, bgcolor: colors.surfaceLow, borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{
                height: '100%', width: `${pct}%`,
                background: gradient.primary, borderRadius: 2,
                transition: 'width 0.25s ease-out',
              }} />
            </Box>
            <Box sx={{
              fontFamily: typography.fontFamilyMono, fontWeight: 600, fontSize: '11px',
              color: colors.textPrimary, letterSpacing: '0.04em',
            }}>
              {reviewedCount} / {total} REVIEWED
            </Box>
          </Box>
        </Box>

        {/* Body scroll */}
        <Box sx={{
          flex: 1, overflowY: 'auto', p: '20px 28px 32px',
          bgcolor: colors.surface, scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': { bgcolor: colors.surfaceLow, borderRadius: 3 },
        }}>
          {isLoading ? (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
              <CircularProgress size={28} sx={{ color: colors.accent }} />
            </Box>
          ) : !report ? (
            <Alert severity="info" sx={{ fontFamily: typography.fontFamily }}>
              No draft report yet for this case. Run agentic analysis or contact your administrator.
            </Alert>
          ) : (
            sections.map((s, i) => {
              const numberLabel = String(i + 1).padStart(2, '0');
              const statusKey = statusMap[s.key] || 'drafted';
              // Special hero for section 02 Recommendation
              const isRecommendation = s.key === 'recommendation' || i === 1;
              if (isRecommendation && report.summary) {
                return (
                  <Box key={s.key} sx={{ borderBottom: `1px solid ${colors.canvas}`, p: '16px 2px' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '24px 1fr auto auto', gap: 1.5, alignItems: 'center', p: '4px 0 12px' }}>
                      <Box sx={{
                        fontFamily: typography.fontFamilyMono, fontSize: '10.5px', fontWeight: 700,
                        letterSpacing: '0.04em',
                        width: 24, height: 24, borderRadius: '50%',
                        bgcolor: colors.primaryContainer, color: colors.onPrimaryContainer,
                        display: 'grid', placeItems: 'center',
                      }}>
                        {numberLabel}
                      </Box>
                      <Typography sx={{
                        fontFamily: typography.fontFamilyDisplay, fontWeight: 700, fontSize: '14.5px',
                        letterSpacing: '-0.005em',
                      }}>
                        {s.title}
                      </Typography>
                      <StatusChip
                        statusKey={statusKey}
                        openMenu={openMenuKey === s.key}
                        onToggle={() => setOpenMenuKey(openMenuKey === s.key ? null : s.key)}
                        onChange={(nk) => { setStatus(s.key, nk); setOpenMenuKey(null); }}
                      />
                      <Box />
                    </Box>
                    <VerdictHero
                      summary={report.summary}
                      confidence={report.confidence ?? 0.94}
                      actionPills={[
                        { label: 'Decline application', variant: 'red' },
                        { label: 'Escalate → Financial Crime' },
                        { label: 'File SMR' },
                      ]}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5, pt: 0.25, fontSize: '11.5px', color: colors.textSecondary }}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                        <AutoAwesomeIcon sx={{
                          color: colors.primary, fontSize: 11,
                          animation: 'sparkle-pulse 2s ease-in-out infinite',
                          '@keyframes sparkle-pulse': { '0%,100%': { opacity: 0.7, transform: 'scale(1)' }, '50%': { opacity: 1, transform: 'scale(1.15)' } },
                        }} />
                        Aggregated from Fraud, Network &amp; Risk Multiplier agents
                      </Box>
                      <Button
                        size="small" startIcon={<ReplayIcon sx={{ fontSize: 11 }} />}
                        sx={{
                          fontFamily: typography.fontFamily, fontSize: '11px', fontWeight: 600,
                          color: colors.primary, textTransform: 'none',
                          '&:hover': { bgcolor: colors.primaryContainer },
                        }}
                      >
                        Regenerate
                      </Button>
                    </Box>
                  </Box>
                );
              }
              return (
                <Section
                  key={s.key}
                  section={s}
                  numberLabel={numberLabel}
                  statusKey={statusKey}
                  setStatus={setStatus}
                  openMenuKey={openMenuKey}
                  setOpenMenuKey={setOpenMenuKey}
                  reportId={report.id}
                  onSave={patch.mutate}
                  busy={patch.isPending}
                />
              );
            })
          )}
        </Box>
      </Box>

      {/* ── Evidence rail ── */}
      {showEvidence && (
      <Box sx={{
        bgcolor: colors.canvas, p: '20px 20px 24px',
        overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5,
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-thumb': { bgcolor: colors.surfaceLow, borderRadius: 3 },
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', px: 0.25 }}>
          <Typography sx={{
            fontFamily: typography.fontFamilyDisplay, fontWeight: 700, fontSize: '14px',
            letterSpacing: '-0.005em',
          }}>
            Evidence Pack
          </Typography>
          <Box sx={{
            fontSize: '10.5px', fontFamily: typography.fontFamilyMono,
            color: colors.textSecondary, letterSpacing: '0.06em',
          }}>
            {attachments.length} ATTACHED
          </Box>
        </Box>

        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          bgcolor: colors.surface, p: '7px 10px', borderRadius: '8px',
          boxShadow: shadows.ambient,
        }}>
          <SearchIcon sx={{ fontSize: 13, color: colors.textSecondary }} />
          <InputBase
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search evidence…"
            sx={{ flex: 1, fontFamily: typography.fontFamily, fontSize: '12px', color: colors.textPrimary, '& input::placeholder': { color: colors.outlineVariant, opacity: 1 } }}
          />
        </Box>

        {filteredAttachments.map((item, i) => {
          const cite = `E${i + 1}`;
          return (
            <EvidenceCard
              key={item.key || item.ref || i}
              item={item} cite={cite}
              usedIn={citedSectionKeys.has(cite)}
            />
          );
        })}

        {filteredAttachments.length === 0 && !isLoading && (
          <Typography sx={{ fontSize: '11px', color: colors.textSecondary, textAlign: 'center', pt: 2 }}>
            {attachments.length === 0 ? 'No evidence attached.' : 'No matches.'}
          </Typography>
        )}
      </Box>
      )}
    </Drawer>
  );
};

export default ReportEditor;
